import { useState, useMemo, FC } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Pagination, Loader } from '../../../../components';
import { Button, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Delete } from '@mui/icons-material';
import { deleteIncentiveType, getIncentiveTypes } from '../../../../fetch';
import { IIncentiveType, IIncentiveTypesRes } from '../../../../models';
import { IncentiveTypeModal } from '../modals/incentive-type-modal';
import { useQuery } from 'react-query';

interface IIncentiveTypesFile {
  isModalOpen: boolean;
  setModalOpen: (val: boolean) => void;
}

export const IncentiveTypes: FC<IIncentiveTypesFile> = ({ isModalOpen, setModalOpen }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [currentIncentiveType, setCurrentIncentiveType] = useState<IIncentiveType>(null);
  const classes = useStyles();

  const [page, setPage] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);

  const {
    isLoading,
    data: incentiveTypes,
    refetch
  } = useQuery<IIncentiveTypesRes, Error>(
    ['get-incentive-types', page, perPage],
    () =>
      getIncentiveTypes({
        isDeleted: false,
        page: page + 1,
        perPage
      }),
    {}
  );

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this incentive type?');
    if (result) {
      setDeleting(true);
      try {
        await deleteIncentiveType(id);
        refetch();
        enqueueSnackbar('Incentive Type Deleted!', {
          variant: 'success'
        });
      } catch (error) {
        var errorMessage = error?.response?.data?.Detail;
        if (errorMessage) {
          enqueueSnackbar(errorMessage, {
            variant: 'error'
          });
        } else {
          enqueueSnackbar('Error, unable to delete incentive type, please try again', {
            variant: 'error'
          });
        }
      } finally {
        setDeleting(false);
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IIncentiveType } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                disabled={isDeleting}
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  setCurrentIncentiveType(original);
                  setModalOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                disabled={isDeleting}
                className={classes.deleteButton}
                startIcon={<Delete />}
                onClick={() => {
                  setCurrentIncentiveType(original);
                  handleDelete(original.id);
                }}
              >
                Delete
              </Button>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incentiveTypes, isDeleting]);

  return (
    <>
      {(isLoading || !incentiveTypes || isDeleting) && (
        <Loader position="centered" type="overlay">
          Loading...
        </Loader>
      )}
      {incentiveTypes && (
        <>
          <Table
            columns={columns}
            data={incentiveTypes?.records}
            isLoading={isLoading}
            hidePagination
            mobileProps={{
              handleEdit: (val: IIncentiveType) => setCurrentIncentiveType(val),
              handleDelete: val => handleDelete(val.territoryId)
            }}
          />
          <Pagination
            page={page}
            count={incentiveTypes?.totalRecordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setPerPage}
          />
        </>
      )}
      <IncentiveTypeModal
        onClose={() => {
          setModalOpen(false);
          setTimeout(() => {
            setCurrentIncentiveType(null);
          }, 500);
        }}
        open={isModalOpen}
        fetchIncentiveTypes={refetch}
        currentIncentiveType={currentIncentiveType}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
